import React, { useState, useRef, useCallback } from 'react';
import { translations } from '../i18n';
import type { Language, Translations } from '../i18n';

function getDeviceLanguage(): Language {
  const nav = navigator.language?.substring(0, 2);
  if (nav === 'nl' || nav === 'fr' || nav === 'de') return nav;
  return 'en';
}

// ── SVG Illustrations ──────────────────────────────────────────

function WelcomeIllustration() {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
      {/* Heart shape */}
      <path
        d="M90 160 C90 160 20 110 20 65 C20 35 45 15 70 15 C82 15 90 25 90 25 C90 25 98 15 110 15 C135 15 160 35 160 65 C160 110 90 160 90 160Z"
        fill="rgba(255,255,255,0.15)"
        stroke="white"
        strokeWidth="3"
      />
      {/* Cross / plus for care */}
      <rect x="80" y="55" width="20" height="50" rx="4" fill="white" opacity="0.9" />
      <rect x="65" y="70" width="50" height="20" rx="4" fill="white" opacity="0.9" />
    </svg>
  );
}

function ConnectedIllustration() {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
      {/* Phone body */}
      <rect x="55" y="30" width="70" height="120" rx="12" stroke="white" strokeWidth="3" fill="rgba(255,255,255,0.1)" />
      <rect x="65" y="45" width="50" height="75" rx="4" fill="rgba(255,255,255,0.15)" />
      {/* Speaker grille */}
      <rect x="78" y="36" width="24" height="3" rx="1.5" fill="rgba(255,255,255,0.5)" />
      {/* Home button */}
      <circle cx="90" cy="138" r="6" stroke="white" strokeWidth="2" fill="none" />
      {/* Signal waves */}
      <path d="M130 50 C140 50 148 58 148 68" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.5" />
      <path d="M130 40 C147 40 160 53 160 70" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.35" />
      <path d="M130 30 C154 30 172 48 172 72" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" opacity="0.2" />
      {/* People icons */}
      <circle cx="78" cy="72" r="8" fill="rgba(255,255,255,0.7)" />
      <circle cx="102" cy="72" r="8" fill="rgba(255,255,255,0.7)" />
      <path d="M66 95 C66 85 78 80 78 80" stroke="rgba(255,255,255,0.5)" strokeWidth="2" fill="none" />
      <path d="M114 95 C114 85 102 80 102 80" stroke="rgba(255,255,255,0.5)" strokeWidth="2" fill="none" />
    </svg>
  );
}

function HomeScreenIllustration() {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
      {/* Grid of widget cards */}
      <rect x="20" y="20" width="65" height="50" rx="8" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="2" />
      <rect x="95" y="20" width="65" height="50" rx="8" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="2" />
      <rect x="20" y="80" width="140" height="35" rx="8" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="2" />
      <rect x="20" y="125" width="65" height="35" rx="8" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="2" />
      <rect x="95" y="125" width="65" height="35" rx="8" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="2" />
      {/* Sun icon in weather widget */}
      <circle cx="52" cy="42" r="10" fill="rgba(255,255,255,0.5)" />
      <line x1="52" y1="26" x2="52" y2="30" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="52" y1="54" x2="52" y2="58" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="36" y1="42" x2="40" y2="42" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="64" y1="42" x2="68" y2="42" stroke="white" strokeWidth="2" strokeLinecap="round" />
      {/* Calendar icon in agenda widget */}
      <rect x="118" y="32" width="20" height="22" rx="3" stroke="white" strokeWidth="2" fill="none" />
      <line x1="118" y1="39" x2="138" y2="39" stroke="white" strokeWidth="2" />
      <line x1="124" y1="28" x2="124" y2="34" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <line x1="132" y1="28" x2="132" y2="34" stroke="white" strokeWidth="2" strokeLinecap="round" />
      {/* News lines */}
      <line x1="35" y1="93" x2="80" y2="93" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
      <line x1="35" y1="100" x2="145" y2="100" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function GetStartedIllustration() {
  return (
    <svg width="180" height="180" viewBox="0 0 180 180" fill="none">
      {/* Large checkmark circle */}
      <circle cx="90" cy="85" r="55" stroke="white" strokeWidth="3" fill="rgba(255,255,255,0.1)" />
      <path
        d="M62 85 L82 105 L118 65"
        stroke="white"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Sparkles */}
      <circle cx="145" cy="45" r="3" fill="rgba(255,255,255,0.6)" />
      <circle cx="35" cy="55" r="2" fill="rgba(255,255,255,0.4)" />
      <circle cx="150" cy="110" r="2.5" fill="rgba(255,255,255,0.5)" />
      <circle cx="30" cy="120" r="2" fill="rgba(255,255,255,0.3)" />
      {/* Arrow pointing forward */}
      <path d="M78 150 L90 162 L102 150" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

// ── Page definitions ───────────────────────────────────────────

interface PageDef {
  titleKey: keyof Translations['onboarding'];
  subtitleKey: keyof Translations['onboarding'];
  Illustration: React.FC;
}

const pages: PageDef[] = [
  { titleKey: 'welcomeTitle', subtitleKey: 'welcomeSubtitle', Illustration: WelcomeIllustration },
  { titleKey: 'connectedTitle', subtitleKey: 'connectedSubtitle', Illustration: ConnectedIllustration },
  { titleKey: 'homeScreenTitle', subtitleKey: 'homeScreenSubtitle', Illustration: HomeScreenIllustration },
  { titleKey: 'getStartedTitle', subtitleKey: 'getStartedSubtitle', Illustration: GetStartedIllustration },
];

// ── Main component ─────────────────────────────────────────────

interface OnboardingWelcomeProps {
  onComplete: () => void;
}

export function OnboardingWelcome({ onComplete }: OnboardingWelcomeProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const lang = getDeviceLanguage();
  const t = translations[lang].onboarding;
  const isLastPage = currentPage === pages.length - 1;

  const goNext = useCallback(() => {
    if (currentPage < pages.length - 1) {
      setDirection('left');
      setCurrentPage(p => p + 1);
    }
  }, [currentPage]);

  const goPrev = useCallback(() => {
    if (currentPage > 0) {
      setDirection('right');
      setCurrentPage(p => p - 1);
    }
  }, [currentPage]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    // Only register horizontal swipes
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) goNext();
      else goPrev();
    }
  }, [goNext, goPrev]);

  const page = pages[currentPage];

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--caro-primary)',
        zIndex: 9998,
        overflow: 'hidden',
        padding: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Page content with animation */}
      <div
        key={currentPage}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          maxWidth: '400px',
          width: '100%',
          animation: direction
            ? `caro-onboarding-slide-${direction} 0.3s ease-out`
            : 'caro-onboarding-fade-in 0.3s ease-out',
        }}
      >
        {/* Illustration */}
        <div style={{ marginBottom: '32px' }}>
          <page.Illustration />
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 'var(--font-heading)',
          fontWeight: 700,
          color: '#FFFFFF',
          margin: '0 0 12px 0',
          lineHeight: 1.2,
        }}>
          {t[page.titleKey]}
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: 'var(--font-body)',
          fontWeight: 400,
          color: 'rgba(255,255,255,0.8)',
          margin: 0,
          lineHeight: 1.5,
          maxWidth: '340px',
        }}>
          {t[page.subtitleKey]}
        </p>
      </div>
      {/* Bottom controls */}
      <div style={{
        position: 'absolute',
        bottom: '48px',
        left: '24px',
        right: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
      }}>
        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > currentPage ? 'left' : 'right');
                setCurrentPage(i);
              }}
              aria-label={`Page ${i + 1}`}
              style={{
                width: i === currentPage ? '24px' : '10px',
                height: '10px',
                borderRadius: '5px',
                border: 'none',
                backgroundColor: i === currentPage ? '#FFFFFF' : 'rgba(255,255,255,0.35)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          ))}
        </div>

        {/* Primary button */}
        <button
          onClick={isLastPage ? onComplete : goNext}
          style={{
            width: '100%',
            maxWidth: '340px',
            padding: '16px 32px',
            fontSize: 'var(--font-body)',
            fontWeight: 600,
            color: 'var(--caro-primary)',
            backgroundColor: '#FFFFFF',
            border: 'none',
            borderRadius: '16px',
            cursor: 'pointer',
            minHeight: '56px',
          }}
        >
          {isLastPage ? t.getStartedButton : t.next}
        </button>

        {/* Skip link (not shown on last page) */}
        {!isLastPage && (
          <button
            onClick={onComplete}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.6)',
              fontSize: 'var(--font-body)',
              cursor: 'pointer',
              padding: '8px 16px',
              minHeight: '44px',
            }}
          >
            {t.skip}
          </button>
        )}
      </div>
      <style>{`
        @keyframes caro-onboarding-slide-left {
          from { opacity: 0; transform: translateX(60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes caro-onboarding-slide-right {
          from { opacity: 0; transform: translateX(-60px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes caro-onboarding-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; }
        }
      `}</style>
    </div>
  );
}
